import { Account } from '@packages/models/api';
import { GOOGLE_ANALYTICS_LIMITS, truncate } from './google-analytics.params';

/**
 * The narrow slice of an account that analytics is permitted to see. It exists so that no other part of the
 * `Account` model can drift into a GA4 payload; anything not listed here never reaches the dataLayer.
 */
export type GoogleAnalyticsUserTraits = {
    /** Opaque account identifier sent as the GA4 `user_id`, used to stitch sessions together across devices. */
    accountId: string;

    /**
     * Raw, comma-separated list of the account's base role descriptions. Kept in its unprocessed form here so the
     * payload builder can decide how to compact it for GA4's user-property limits.
     */
    baseRolesDescription?: string;
};

/**
 * Narrows a full account down to the non-PII traits analytics is allowed to see. This is the single choke point
 * where account data crosses into the analytics layer, so the field list is deliberately explicit rather than a
 * spread or an omission list; new account fields cannot leak by accident.
 *
 * @param account - the signed-in account, or `null` when no user is authenticated
 *
 * @returns the analytics-safe traits, or `null` when there is no account to describe
 *
 * @example
 * const traits = toGoogleAnalyticsUserTraits(account);
 * // { accountId: 'acct-1', baseRolesDescription: 'TECHNICIAN,MANAGER' }
 */
export function toGoogleAnalyticsUserTraits(account: Account | null): GoogleAnalyticsUserTraits | null {
    if (!account) {
        return null;
    }
    return {
        accountId: account.accountId,
        baseRolesDescription: account.baseRolesDescription,
    };
}
/**
 * Flat, not nested as GTM reads each user property field as a separate user property.
 */
export function buildIdentifyPayload(traits: GoogleAnalyticsUserTraits | null): Record<string, unknown> {
    return {
        user_id: traits ? truncate(traits.accountId, GOOGLE_ANALYTICS_LIMITS.USER_ID_MAX_LENGTH) : null,
        user_access_roles: traits ? buildAccessRoles(traits.baseRolesDescription) : null,
    };
}

/**
 * Comma-joined, sorted, length-capped list of access-role identifiers. Whole
 * roles are dropped (never truncated mid-token) when the 36-char GA4 limit
 * is reached, so the value never corrupts mid-identifier.
 */
function buildAccessRoles(baseRoles: string | undefined): string | null {
    if (!baseRoles) {
        return null;
    }

    // Split on commas, trim whitespace, dedupe, sort, and pack to fit GA4's 36-char limit.
    const compacted = baseRoles
        .split(',')
        .map((role) => role.trim().replace(/\s+/g, ''))
        .filter(Boolean);

    const roles = Array.from(new Set(compacted)).sort();

    const included: string[] = [];
    let length = 0;
    for (const role of roles) {
        const projected = length + (included.length === 0 ? 0 : 1) + role.length;
        if (projected > GOOGLE_ANALYTICS_LIMITS.USER_PROPERTY_VALUE_MAX_LENGTH) {
            continue;
        }
        included.push(role);
        length = projected;
    }

    return included.join(',') || null;
}

/**
 * Compares two trait sets for equality. Identify calls can fire on any render or auth refresh, so this guard lets
 * the service skip pushing a duplicate identify payload to the dataLayer when nothing about the user has changed.
 *
 * @param a - the previously attached traits, or `null` if none were attached
 * @param b - the traits being considered for attachment, or `null` when the user signed out
 *
 * @returns `true` when both sides describe the same user (including both being `null`), otherwise `false`
 *
 * @example
 * if (hasSameTraits(this.attachedTraits, traits)) {
 *     return; // nothing changed; do not push another identify payload
 * }
 */
export function hasSameTraits(a: GoogleAnalyticsUserTraits | null, b: GoogleAnalyticsUserTraits | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.accountId === b.accountId && a.baseRolesDescription === b.baseRolesDescription;
}
